import { notification } from 'antd';
import axios from 'axios';
import { persistor, store } from '../store';
import { logout, refreshAuth } from '../redux/action-creators';
import history from '../history';

// 401s from these endpoints mean "credentials/code are wrong", not "session is dead",
// so they should surface a toast instead of silently logging the user out.
const CREDENTIAL_ENDPOINTS = [
  '/user/login',
  '/user/create',
  '/user/resetPassword',
  '/user/resetPasswordRequest'
];

const isCredentialEndpoint = (url) => {
  if (!url) return false;
  return CREDENTIAL_ENDPOINTS.some((path) => url.endsWith(path));
};

const MAX_DESCRIPTION_LENGTH = 240;
const GENERIC_DESCRIPTION = 'Something went wrong. Please try again.';

const truncate = (str) => {
  if (str.length <= MAX_DESCRIPTION_LENGTH) return str;
  return str.slice(0, MAX_DESCRIPTION_LENGTH - 1) + '…';
};

// Single-flight refresh: if many requests fail with 401 at once, only one
// /auth/refresh round-trip should fly. All callers wait on the same promise.
let refreshPromise = null;
const refreshOnce = () => {
  if (!refreshPromise) {
    refreshPromise = Promise.resolve()
      .then(() => refreshAuth()(store.dispatch, store.getState))
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const isAuthRoutingEndpoint = (url) =>
  !!url && (/\/auth\/refresh$/.test(url) || /\/auth\/logout$/.test(url));

const formatErrorDescription = (data) => {
  if (data == null) return GENERIC_DESCRIPTION;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed === '') return GENERIC_DESCRIPTION;
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('<!doctype') || lower.startsWith('<html')) {
      return GENERIC_DESCRIPTION;
    }
    return truncate(trimmed);
  }
  if (typeof data === 'object' && typeof data.message === 'string') {
    return truncate(data.message);
  }
  return GENERIC_DESCRIPTION;
};

class ErrorMiddleware {
  async onResponseError(error) {
    // A request cancelled by a newer request superseding it (e.g.
    // updateDataAndFlush minting a fresh CancelToken on every call —
    // polling-context.js) is not a real failure; nothing was actually wrong
    // with the network or the server. /session/poll and /session/setData
    // already mark themselves `_silent` and swallow their own rejections, so
    // this guard's real value is any other, non-silent caller that cancels a
    // request — without it, that caller would surface a spurious "Network
    // error" toast for a cancellation it initiated itself (PIN-198 #7).
    // Mirrors the existing check in content-editor-page.js.
    if (axios.isCancel(error) || error.name === 'AbortError') {
      throw error;
    }

    const requestUrl = error.config && error.config.url;
    const isAuthFailure = error.response && error.response.status === 401;
    const alreadyRefreshed = !!(error.config && error.config._refreshed);

    // The login thunk owns its own error UX (classified toast in action-creators.js).
    if (requestUrl && requestUrl.endsWith('/user/login')) {
      throw error;
    }

    // /auth/refresh and /auth/logout failures must NOT trigger another
    // refresh attempt — that would loop or fight a server-side revoke.
    // Refresh-then-retry path: 401 on a protected endpoint where we have
    // a config to retry against and we haven't already retried.
    if (
      isAuthFailure &&
      error.config &&
      !isCredentialEndpoint(requestUrl) &&
      !isAuthRoutingEndpoint(requestUrl) &&
      !alreadyRefreshed
    ) {
      try {
        const newToken = await refreshOnce();
        // error.config is the *post-merge* axios config (baseURL already
        // applied, defaults flattened into headers), so re-issuing it
        // through axios.request preserves withCredentials and any other
        // top-level keys the original call set. If axios defaults grow
        // richer in the future (interceptor-set keys, transformers
        // installed only at call time), revisit this and consider replaying
        // the *user-supplied* config instead.
        const retryConfig = {
          ...error.config,
          headers: {
            ...(error.config.headers || {}),
            Authorization: 'Token ' + newToken
          },
          _refreshed: true
        };
        return axios.request(retryConfig);
      } catch (e) {
        // refresh failed — fall through to the existing logout flow
      }
    }

    // logout and redirect to login page if the API rejected our token on a protected endpoint
    if (isAuthFailure && !isCredentialEndpoint(requestUrl)) {
      // If we got here from N parallel 401s sharing a single failed refresh,
      // only the first caller should run the logout flow — the awaited
      // dispatch(logout()) below clears the token, so subsequent callers
      // see an empty token and bail out cleanly. Without this, the user
      // sees N stacked 'session expired' toasts and the client fires N
      // redundant /auth/logout POSTs.
      if (!store.getState().token) {
        throw error;
      }
      // Await the thunk so the logout action has actually been applied
      // before we flush persistor and redirect; otherwise persistor.flush
      // can write the pre-logout state (with the stale token) to storage.
      await store.dispatch(logout());
      await persistor.flush();
      notification.warning({
        message: 'Your session has expired. Please sign in again.'
      });
      history.push('/');
      throw error;
    }

    // _silent opts callers (e.g. the 1 Hz polling loop) out of the network /
    // generic toast path. The 401 logout/refresh flow above is intentionally
    // NOT gated on this — auth state must still be cleared if a polling
    // request reveals an expired token.
    const silent = !!(error.config && error.config._silent);

    // No response means a network error (offline, DNS failure, timeout). Surface a
    // generic network-error toast instead of dereferencing error.response.status.
    if (!error.response) {
      if (!silent) {
        notification.error({
          message: 'Network error',
          description: 'Could not reach the server. Please check your connection and try again.'
        });
      }
      throw error;
    }

    // notify the user of the error
    if (!silent) {
      notification.error({
        message: 'Error (' + error.response.status + '):',
        description: formatErrorDescription(error.response.data)
      });
    }
    throw error;
  }
}

export default ErrorMiddleware;
